import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Switch,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { AppContext } from 'AppContext';

const PREFIX = 'MailDomainSettings';

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  [`& .${PREFIX}-card`]: {
    maxWidth: 600,
  },
  [`& .${PREFIX}-actions`]: {
    justifyContent: 'flex-start',
    padding: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const defaultFormState = {
  smtpHost: '',
  smtpPort: 587,
  smtpUsername: '',
  smtpPassword: '',
  senderEmail: '',
  senderName: '',
  useSsl: false,
  useStarttls: true,
  enabled: false,
};

const MailDomainSettings = () => {
  const appContext = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  const [formState, setFormState] = useState(defaultFormState);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    appContext.mailDomainApi
      .getMailDomain()
      .then(({ data }) => {
        setFormState({ ...data, smtpPassword: '' });
        setSaved(true);
        setLoading(false);
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          setLoading(false);
          return;
        }
        appContext.errorHandler(error);
        setLoading(false);
      });
  }, []);

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    const dataToSend = { ...formState };
    if (saved && !dataToSend.smtpPassword) {
      delete dataToSend.smtpPassword;
    }
    appContext.mailDomainApi
      .saveMailDomain(dataToSend)
      .then(({ data }) => {
        setFormState({ ...data, smtpPassword: '' });
        setSaved(true);
        enqueueSnackbar('Настройки сохранены', { variant: 'success' });
      })
      .catch(error => {
        const msg = error.response?.data?.message || 'Ошибка сохранения';
        enqueueSnackbar(msg, { variant: 'error' });
      });
  };

  const handleTest = () => {
    appContext.mailDomainApi
      .testMailDomain({})
      .then(({ data }) => {
        enqueueSnackbar(`Тестовое письмо отправлено на ${data.to}`, { variant: 'success' });
      })
      .catch(error => {
        const msg = error.response?.data?.message || 'Ошибка отправки тестового письма';
        enqueueSnackbar(msg, { variant: 'error' });
      });
  };

  const handleDelete = () => {
    if (!window.confirm('Удалить настройки почты?')) return;
    appContext.mailDomainApi
      .deleteMailDomain()
      .then(() => {
        setFormState(defaultFormState);
        setSaved(false);
        enqueueSnackbar('Настройки удалены', { variant: 'success' });
      })
      .catch(error => {
        const msg = error.response?.data?.message || 'Ошибка удаления';
        enqueueSnackbar(msg, { variant: 'error' });
      });
  };

  if (loading) return null;

  return (
    <Root>
      <Card className={`${PREFIX}-card`}>
        <CardHeader title='Настройки почты' subheader='SMTP-настройки для отправки писем посетителям' />
        <Divider />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label='SMTP Хост'
            name='smtpHost'
            value={formState.smtpHost}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            label='SMTP Порт'
            name='smtpPort'
            type='number'
            value={formState.smtpPort}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            label='Имя пользователя'
            name='smtpUsername'
            value={formState.smtpUsername}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            label='Пароль'
            name='smtpPassword'
            type='password'
            value={formState.smtpPassword}
            onChange={handleChange}
            placeholder={saved ? 'Пароль задан' : ''}
          />
          <TextField
            fullWidth
            label='Email отправителя'
            name='senderEmail'
            value={formState.senderEmail}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            label='Имя отправителя'
            name='senderName'
            value={formState.senderName}
            onChange={handleChange}
          />
          <FormControlLabel
            control={<Checkbox checked={formState.useSsl} onChange={handleChange} name='useSsl' />}
            label='SSL'
          />
          <FormControlLabel
            control={<Checkbox checked={formState.useStarttls} onChange={handleChange} name='useStarttls' />}
            label='STARTTLS'
          />
          <FormControlLabel
            control={<Switch checked={formState.enabled} onChange={handleChange} name='enabled' />}
            label='Включено'
          />
        </CardContent>
        <Divider />
        <CardActions className={`${PREFIX}-actions`}>
          <Button variant='contained' color='primary' onClick={handleSave}>
            Сохранить
          </Button>
          <Button variant='outlined' onClick={handleTest} disabled={!saved}>
            Отправить тестовое письмо
          </Button>
          {saved && (
            <Button variant='outlined' color='error' onClick={handleDelete}>
              Удалить настройки
            </Button>
          )}
        </CardActions>
      </Card>
    </Root>
  );
};

export default MailDomainSettings;
